import { useToast } from "primevue/usetoast";
import {
  AccountDeletionErrorReason,
  AccountDeletionOutcome,
  type AccountDeletionPreviewInterface,
  type AccountDeletionRequestInterface,
  type ScheduledDeletionInterface,
} from "shared-core/types/account-deletion";
import { formatDate } from "shared-core/utils/format";
import { inject, type Ref, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  type AuthStore,
  type AuthStoreActions,
  AuthStoreInjectKey,
} from "../types/auth-store";
import { resolveApiErrorDetail } from "../utils/admin-helpers";
import { useApi } from "../utils/api";

export enum DeletionTarget {
  ACCOUNT = "account",
  ORGANIZATION = "organization",
}

export type DeletionRequest =
  | { target: DeletionTarget.ACCOUNT; outcome: AccountDeletionOutcome }
  | { target: DeletionTarget.ORGANIZATION };

export function useAccountDeletion(): {
  request: Ref<DeletionRequest | null>;
  submitting: Ref<boolean>;
  errorMessage: Ref<string | null>;
  openAccountDeletion: () => Promise<void>;
  openOrganizationDeletion: () => void;
  close: () => void;
  confirm: () => Promise<void>;
} {
  const api = useApi();
  const toast = useToast();
  const i18n = useI18n();
  const { t, locale } = i18n;
  const te = i18n.te.bind(i18n);
  const authStore = inject<AuthStore & AuthStoreActions>(AuthStoreInjectKey)!;

  const request = ref<DeletionRequest | null>(null);
  const submitting = ref<boolean>(false);
  const errorMessage = ref<string | null>(null);

  function open(next: DeletionRequest): void {
    errorMessage.value = null;
    request.value = next;
  }

  async function openAccountDeletion(): Promise<void> {
    try {
      const response =
        await api.get<AccountDeletionPreviewInterface>("account/deletion");
      open({
        target: DeletionTarget.ACCOUNT,
        outcome: (await response.data).outcome,
      });
    } catch (error) {
      console.error("Failed to preview the account deletion", error);
      toast.add({
        severity: "error",
        summary: t("common.error"),
        detail: t("shared_ui.account_deletion.preview_failed"),
        life: 5000,
      });
    }
  }

  function openOrganizationDeletion(): void {
    open({ target: DeletionTarget.ORGANIZATION });
  }

  function close(): void {
    if (!submitting.value) request.value = null;
  }

  async function showFailure(error: unknown): Promise<void> {
    if (
      error instanceof Error &&
      error.message === AccountDeletionErrorReason.OUTCOME_CHANGED.toString()
    ) {
      await openAccountDeletion();
    }
    errorMessage.value = resolveApiErrorDetail(error, t, te);
  }

  async function submit(
    current: DeletionRequest,
  ): Promise<ScheduledDeletionInterface | null> {
    submitting.value = true;
    errorMessage.value = null;
    try {
      const response = await api.delete<ScheduledDeletionInterface>(
        current.target,
        current.target === DeletionTarget.ACCOUNT
          ? {
              json: {
                outcome: current.outcome,
              } satisfies AccountDeletionRequestInterface,
            }
          : undefined,
      );
      return await response.data;
    } catch (error) {
      await showFailure(error);
      return null;
    } finally {
      submitting.value = false;
    }
  }

  async function confirm(): Promise<void> {
    const current = request.value;
    if (!current || submitting.value) return;

    const scheduled = await submit(current);
    if (!scheduled) return;

    request.value = null;
    const scheduledTarget =
      current.target === DeletionTarget.ORGANIZATION ||
      current.outcome === AccountDeletionOutcome.ORGANIZATION
        ? DeletionTarget.ORGANIZATION
        : DeletionTarget.ACCOUNT;
    toast.add({
      severity: "success",
      summary: t(`shared_ui.account_deletion.${scheduledTarget}.scheduled`),
      detail: t(
        `shared_ui.account_deletion.${scheduledTarget}.scheduled_detail`,
        { date: formatDate(scheduled.deleteAt, locale.value, "long") },
      ),
      life: 10000,
    });
    // The deletion is scheduled by now, so a failed refresh must not read as a failed deletion.
    await authStore.checkAuth(true).catch((error: unknown) => {
      console.error("Failed to refresh the user after the deletion", error);
    });
  }

  return {
    request,
    submitting,
    errorMessage,
    openAccountDeletion,
    openOrganizationDeletion,
    close,
    confirm,
  };
}
